import { useState, useEffect, useCallback, useRef } from "react";
import { useTeams } from "../lib/teams";
import api from "../lib/api";
import { useAiJob } from "../lib/useAiJob";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Sparkles, Dumbbell, BarChart3, ClipboardCheck, MessageSquare, Loader2, Copy, Wand2, History } from "lucide-react";
import { toast } from "sonner";
import AILoadingState from "../components/ai/AILoadingState";
import AIHistoryTab from "../components/ai/AIHistoryTab";
import UpgradeBanner from "../components/billing/UpgradeBanner";
import { usePlan } from "../lib/usePlan";

export default function MisterAIPage() {
  const { activeTeam, canEdit } = useTeams();
  const plan = usePlan();
  const [tab, setTab] = useState("training");
  const rerunRequestRef = useRef(null);

  if (!activeTeam) return <p className="text-slate-500">Seleziona una squadra.</p>;
  if (!canEdit) return <p className="text-slate-500">Sezione riservata a Mister e Dirigenti.</p>;

  const aiUnlocked = plan?.has?.("ai");

  const handleRerun = (job) => {
    rerunRequestRef.current = job;
    const map = {
      training: "training",
      "match-analysis": "match",
      "callup-suggestion": "callup",
      "parents-message": "message",
    };
    const target = map[job.kind] || "training";
    setTab(target);
    toast.info("Parametri caricati. Premi rigenera quando vuoi.");
  };

  if (!aiUnlocked && !plan.loading) {
    return (
      <div className="space-y-6" data-testid="mister-ai-page">
        <div>
          <div className="label-eyebrow flex items-center gap-1.5">
            <Sparkles size={12} /> Powered by Claude Sonnet 4.5
          </div>
          <h1 className="font-heading text-4xl font-bold tracking-tight mt-1">Mister AI</h1>
          <p className="text-slate-500">Assistente intelligente per allenamenti, analisi partite, convocazioni e messaggi famiglie.</p>
        </div>
        <UpgradeBanner feature="Mister AI" requiredPlan="pro" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="mister-ai-page">
      <div>
        <div className="label-eyebrow flex items-center gap-1.5">
          <Sparkles size={12} /> Powered by Claude Sonnet 4.5
        </div>
        <h1 className="font-heading text-4xl font-bold tracking-tight mt-1">Mister AI</h1>
        <p className="text-slate-500">Assistente intelligente per allenamenti, analisi partite, convocazioni e messaggi famiglie.</p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-slate-100 flex-wrap h-auto">
          <TabsTrigger value="training" data-testid="ai-tab-training"><Dumbbell size={14} className="mr-1" /> Allenamento</TabsTrigger>
          <TabsTrigger value="match" data-testid="ai-tab-match"><BarChart3 size={14} className="mr-1" /> Analisi partita</TabsTrigger>
          <TabsTrigger value="callup" data-testid="ai-tab-callup"><ClipboardCheck size={14} className="mr-1" /> Convocazione</TabsTrigger>
          <TabsTrigger value="message" data-testid="ai-tab-message"><MessageSquare size={14} className="mr-1" /> Messaggio</TabsTrigger>
          <TabsTrigger value="history" data-testid="ai-tab-history"><History size={14} className="mr-1" /> Cronologia</TabsTrigger>
        </TabsList>
        <TabsContent value="training" className="mt-4"><TrainingTab team={activeTeam} rerunRequestRef={rerunRequestRef} activeTab={tab} /></TabsContent>
        <TabsContent value="match" className="mt-4"><MatchTab team={activeTeam} rerunRequestRef={rerunRequestRef} activeTab={tab} /></TabsContent>
        <TabsContent value="callup" className="mt-4"><CallupTab team={activeTeam} rerunRequestRef={rerunRequestRef} activeTab={tab} /></TabsContent>
        <TabsContent value="message" className="mt-4"><MessageTab rerunRequestRef={rerunRequestRef} activeTab={tab} /></TabsContent>
        <TabsContent value="history" className="mt-4"><AIHistoryTab team={activeTeam} onRerun={handleRerun} /></TabsContent>
      </Tabs>
    </div>
  );
}

const copyText = async (t) => {
  try { await navigator.clipboard.writeText(t); toast.success("Copiato"); }
  catch (e) { console.warn(e); toast.error("Errore copia"); }
};

// ============================================================
// 1) Training generator
// ============================================================
function TrainingTab({ team, rerunRequestRef, activeTab }) {
  const [focus, setFocus] = useState("");
  const [duration, setDuration] = useState(90);
  const [nDrills, setNDrills] = useState(4);
  const [out, setOut] = useState(null);
  const { run, loading, elapsedSeconds } = useAiJob();

  // Apply rerun payload when we land on this tab via "Rigenera"
  useEffect(() => {
    if (activeTab !== "training") return;
    const job = rerunRequestRef?.current;
    if (!job || job.kind !== "training") return;
    const p = job.payload || {};
    if (p.focus) setFocus(p.focus); // eslint-disable-line react-hooks/set-state-in-effect
    if (p.duration_min) setDuration(p.duration_min); // eslint-disable-line react-hooks/set-state-in-effect
    if (p.n_drills) setNDrills(p.n_drills); // eslint-disable-line react-hooks/set-state-in-effect
    rerunRequestRef.current = null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const onRun = async () => {
    if (!focus.trim()) return toast.error("Indica il focus della seduta");
    setOut(null);
    try {
      const result = await run(`/ai/training`, {
        team_id: team.id, category: team.category,
        focus, duration_min: duration, n_drills: nDrills,
      });
      setOut(result);
      toast.success(`Allenamento generato in ${elapsedSeconds}s`);
    } catch (e) {
      toast.error(`Errore: ${e?.message || "generazione fallita"}`);
    }
  };

  const importToTrainings = async () => {
    if (!out) return;
    const drills = (out.drills || []).map((d) => ({
      name: d.name, duration_min: d.duration_min, description: d.description, key_points: d.key_points || [],
    }));
    try {
      await api.post(`/trainings`, {
        team_id: team.id,
        title: out.title || "Seduta generata da Mister AI",
        date: new Date().toISOString().slice(0, 10),
        duration_min: out.duration_min || duration,
        objectives: out.objectives || "",
        drills,
        season: team.season || null,
      });
      toast.success("Allenamento creato!");
    } catch (e) {
      toast.error("Errore salvataggio allenamento");
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
      <Card className="p-5 md:col-span-2 border-slate-200">
        <h3 className="font-heading font-semibold mb-3">Imposta</h3>
        <div className="space-y-3">
          <div>
            <Label>Categoria</Label>
            <Input value={team.category || ""} disabled />
          </div>
          <div>
            <Label>Focus della seduta *</Label>
            <Textarea
              rows={3}
              value={focus}
              onChange={(e) => setFocus(e.target.value)}
              placeholder="Es. Possesso palla, pressing offensivo, transizione difesa-attacco..."
              data-testid="ai-training-focus"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Durata (min)</Label>
              <Input type="number" min="30" max="180" value={duration} onChange={(e) => setDuration(Number(e.target.value))} />
            </div>
            <div>
              <Label>N° esercitazioni</Label>
              <Input type="number" min="2" max="8" value={nDrills} onChange={(e) => setNDrills(Number(e.target.value))} />
            </div>
          </div>
          <Button onClick={onRun} disabled={loading} className="w-full bg-sky-500 hover:bg-sky-600" data-testid="ai-training-generate">
            {loading ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Wand2 size={14} className="mr-1" />}
            {loading ? `Generazione... ${elapsedSeconds}s` : "Genera seduta"}
          </Button>
        </div>
      </Card>

      <Card className="p-5 md:col-span-3 border-slate-200 min-h-[300px]">
        {!out && !loading && <p className="text-sm text-slate-400">Compila il focus e premi &quot;Genera seduta&quot;.</p>}
        {loading && <AILoadingState label="Generazione allenamento" expectedSeconds={45} />}
        {out && (
          <div className="space-y-4" data-testid="ai-training-output">
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1">
                <h3 className="font-heading font-bold text-xl">{out.title}</h3>
                <p className="text-xs text-slate-500 mt-1">{out.duration_min} min</p>
              </div>
              <div className="flex gap-1">
                <Button size="sm" variant="outline" onClick={() => copyText(JSON.stringify(out, null, 2))}><Copy size={12} /></Button>
                <Button size="sm" onClick={importToTrainings} className="bg-emerald-500 hover:bg-emerald-600" data-testid="ai-import-training">
                  Importa in Allenamenti
                </Button>
              </div>
            </div>
            {out.objectives && (
              <div className="p-3 bg-sky-50 rounded-lg text-sm">
                <strong>Obiettivi: </strong>{out.objectives}
              </div>
            )}
            {out.warm_up && (
              <div>
                <h4 className="font-semibold text-sm mb-1">Riscaldamento</h4>
                <p className="text-sm text-slate-600 whitespace-pre-line">{out.warm_up}</p>
              </div>
            )}
            <div>
              <h4 className="font-semibold text-sm mb-2">Esercitazioni</h4>
              <div className="space-y-2">
                {(out.drills || []).map((d) => (
                  <div key={d.name} className="p-3 rounded-lg border border-slate-200">
                    <div className="flex justify-between items-start gap-2">
                      <strong className="text-sm">{d.name}</strong>
                      <span className="text-xs text-slate-500">{d.duration_min} min</span>
                    </div>
                    <p className="text-xs text-slate-600 mt-1 whitespace-pre-line">{d.description}</p>
                    {d.key_points?.length > 0 && (
                      <ul className="mt-2 text-xs text-slate-500 list-disc list-inside">
                        {d.key_points.map((k) => <li key={k}>{k}</li>)}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            </div>
            {out.cool_down && (
              <div>
                <h4 className="font-semibold text-sm mb-1">Defaticamento</h4>
                <p className="text-sm text-slate-600 whitespace-pre-line">{out.cool_down}</p>
              </div>
            )}
            {out.coach_notes && (
              <div className="p-3 bg-amber-50 rounded-lg text-xs">
                <strong>Note Mister: </strong>{out.coach_notes}
              </div>
            )}
          </div>
        )}
      </Card>
    </div>
  );
}

// ============================================================
// 2) Match analysis
// ============================================================
function MatchTab({ team, rerunRequestRef, activeTab }) {
  const [convocations, setConvocations] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [out, setOut] = useState(null);
  const { run, loading, elapsedSeconds } = useAiJob();

  const load = useCallback(async () => {
    const { data } = await api.get(`/teams/${team.id}/convocations`).catch(() => ({ data: [] }));
    const past = data.filter((c) => c.match_date && c.match_date < new Date().toISOString().slice(0, 10));
    setConvocations(past.slice(0, 20));
  }, [team.id]);
  useEffect(() => { load(); }, [load]);

  // Rerun pre-fill
  useEffect(() => {
    if (activeTab !== "match") return;
    const job = rerunRequestRef?.current;
    if (!job || job.kind !== "match-analysis") return;
    const p = job.payload || {};
    if (p.convocation_id) setSelectedId(p.convocation_id); // eslint-disable-line react-hooks/set-state-in-effect
    rerunRequestRef.current = null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const onRun = async () => {
    if (!selectedId) return toast.error("Seleziona una partita");
    setOut(null);
    try {
      const result = await run(`/ai/match-analysis`, { convocation_id: selectedId });
      setOut(result);
    } catch (e) {
      toast.error(`Errore: ${e?.message || "analisi fallita"}`);
    }
  };

  return (
    <div className="space-y-4">
      <Card className="p-5 border-slate-200">
        <div className="flex items-end gap-3 flex-wrap">
          <div className="flex-1 min-w-[250px]">
            <Label>Partita da analizzare</Label>
            <Select value={selectedId} onValueChange={setSelectedId}>
              <SelectTrigger data-testid="ai-match-select"><SelectValue placeholder="Seleziona..." /></SelectTrigger>
              <SelectContent>
                {convocations.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.match_date} vs {c.opponent || c.title} {c.final_score_us != null ? `(${c.final_score_us}-${c.final_score_them})` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={onRun} disabled={loading || !selectedId} className="bg-sky-500 hover:bg-sky-600" data-testid="ai-match-run">
            {loading ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Wand2 size={14} className="mr-1" />}
            {loading ? `Analizzo... ${elapsedSeconds}s` : "Analizza"}
          </Button>
        </div>
        {convocations.length === 0 && <p className="text-xs text-slate-400 mt-3">Nessuna partita giocata trovata.</p>}
      </Card>

      {loading && <AILoadingState label="Analisi partita in corso" expectedSeconds={45} />}

      {out && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="ai-match-output">
          <Card className="p-5 border-slate-200 md:col-span-2">
            <h3 className="font-heading font-semibold mb-2">Riassunto</h3>
            <p className="text-sm text-slate-600">{out.summary}</p>
          </Card>
          <Card className="p-5 border-emerald-200 bg-emerald-50/30">
            <h3 className="font-heading font-semibold mb-2 text-emerald-700">✓ Cosa è andato bene</h3>
            <ul className="space-y-1 text-sm">
              {(out.what_worked || []).map((x) => <li key={x} className="text-slate-700">• {x}</li>)}
            </ul>
          </Card>
          <Card className="p-5 border-amber-200 bg-amber-50/30">
            <h3 className="font-heading font-semibold mb-2 text-amber-700">⚠ Da migliorare</h3>
            <ul className="space-y-1 text-sm">
              {(out.what_to_improve || []).map((x) => <li key={x} className="text-slate-700">• {x}</li>)}
            </ul>
          </Card>
          <Card className="p-5 border-sky-200 bg-sky-50/30 md:col-span-2">
            <h3 className="font-heading font-semibold mb-2 text-sky-700">Focus prossimo allenamento</h3>
            <ul className="space-y-1 text-sm">
              {(out.next_training_focus || []).map((x) => <li key={x} className="text-slate-700">→ {x}</li>)}
            </ul>
          </Card>
          {(out.individual_highlights || []).length > 0 && (
            <Card className="p-5 border-slate-200 md:col-span-2">
              <h3 className="font-heading font-semibold mb-2">Osservazioni individuali</h3>
              <div className="space-y-2 text-sm">
                {out.individual_highlights.map((h) => (
                  <div key={h.player} className="flex gap-2"><strong className="min-w-[140px]">{h.player}</strong><span className="text-slate-600">{h.note}</span></div>
                ))}
              </div>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================
// 3) Callup suggestion
// ============================================================
function CallupTab({ team, rerunRequestRef, activeTab }) {
  const [opponent, setOpponent] = useState("");
  const [notes, setNotes] = useState("");
  const [out, setOut] = useState(null);
  const [players, setPlayers] = useState([]);
  const { run, loading, elapsedSeconds } = useAiJob();

  const load = useCallback(async () => {
    const { data } = await api.get(`/teams/${team.id}/players`).catch(() => ({ data: [] }));
    setPlayers(data);
  }, [team.id]);
  useEffect(() => { load(); }, [load]);

  // Rerun pre-fill
  useEffect(() => {
    if (activeTab !== "callup") return;
    const job = rerunRequestRef?.current;
    if (!job || job.kind !== "callup-suggestion") return;
    const p = job.payload || {};
    if (p.opponent) setOpponent(p.opponent); // eslint-disable-line react-hooks/set-state-in-effect
    rerunRequestRef.current = null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const playerMap = Object.fromEntries(players.map((p) => [p.id, p]));
  const lbl = (id) => {
    const p = playerMap[id];
    return p ? `${p.last_name} ${p.first_name}` : id;
  };

  const onRun = async () => {
    setOut(null);
    try {
      const result = await run(`/ai/callup-suggestion`, { team_id: team.id, opponent, notes });
      setOut(result);
    } catch (e) {
      toast.error(`Errore: ${e?.message || "fallito"}`);
    }
  };

  return (
    <div className="space-y-4">
      <Card className="p-5 border-slate-200">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <Label>Avversario</Label>
            <Input value={opponent} onChange={(e) => setOpponent(e.target.value)} data-testid="ai-callup-opponent" />
          </div>
          <div className="md:col-span-2">
            <Label>Note (es. infortuni, indisponibili)</Label>
            <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Mario assente, focus sui giovani..." />
          </div>
        </div>
        <Button onClick={onRun} disabled={loading} className="mt-3 bg-sky-500 hover:bg-sky-600" data-testid="ai-callup-run">
          {loading ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Wand2 size={14} className="mr-1" />}
          {loading ? `Analisi... ${elapsedSeconds}s` : "Suggerisci convocazione"}
        </Button>
      </Card>

      {loading && <AILoadingState label="Analisi convocazione" expectedSeconds={30} />}

      {out && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="ai-callup-output">
          <Card className="p-5 border-emerald-200 bg-emerald-50/30">
            <h3 className="font-heading font-semibold mb-2 text-emerald-700">Convocati ({(out.called_up || []).length})</h3>
            <ul className="space-y-1 text-sm">
              {(out.called_up || []).map((p) => (
                <li key={p.player_id}><strong>{lbl(p.player_id)}</strong> <span className="text-xs text-slate-500">— {p.reason}</span></li>
              ))}
            </ul>
          </Card>
          <Card className="p-5 border-amber-200 bg-amber-50/30">
            <h3 className="font-heading font-semibold mb-2 text-amber-700">Borderline</h3>
            <ul className="space-y-1 text-sm">
              {(out.borderline || []).map((p) => (
                <li key={p.player_id}><strong>{lbl(p.player_id)}</strong> <span className="text-xs text-slate-500">— {p.reason}</span></li>
              ))}
            </ul>
          </Card>
          <Card className="p-5 border-slate-200">
            <h3 className="font-heading font-semibold mb-2 text-slate-600">Non convocati</h3>
            <ul className="space-y-1 text-sm">
              {(out.not_called || []).map((p) => (
                <li key={p.player_id}><strong>{lbl(p.player_id)}</strong> <span className="text-xs text-slate-500">— {p.reason}</span></li>
              ))}
            </ul>
          </Card>
          {out.rationale && (
            <Card className="p-4 md:col-span-3 border-sky-200 bg-sky-50/30 text-sm">
              <strong className="text-sky-700">Logica: </strong>{out.rationale}
            </Card>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================
// 4) Parents message draft
// ============================================================
function MessageTab({ rerunRequestRef, activeTab }) {
  const [ctx, setCtx] = useState("");
  const [tone, setTone] = useState("cordiale");
  const [out, setOut] = useState("");
  const { run, loading, elapsedSeconds } = useAiJob();

  // Rerun pre-fill (message-type only carries tone, the original context isn't stored)
  useEffect(() => {
    if (activeTab !== "message") return;
    const job = rerunRequestRef?.current;
    if (!job || job.kind !== "parents-message") return;
    const p = job.payload || {};
    if (p.tone) setTone(p.tone); // eslint-disable-line react-hooks/set-state-in-effect
    rerunRequestRef.current = null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const onRun = async () => {
    if (!ctx.trim()) return toast.error("Inserisci il contesto");
    setOut("");
    try {
      const result = await run(`/ai/parents-message`, { context: ctx, tone });
      setOut(result?.text || "");
    } catch (e) {
      toast.error(`Errore: ${e?.message || "generazione fallita"}`);
    }
  };

  const shareWA = () => {
    if (!out) return;
    window.open(`https://wa.me/?text=${encodeURIComponent(out)}`, "_blank");
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <Card className="p-5 border-slate-200">
        <div className="space-y-3">
          <div>
            <Label>Cosa vuoi comunicare? *</Label>
            <Textarea rows={5} value={ctx} onChange={(e) => setCtx(e.target.value)} placeholder="Es. Allenamento di mercoledì spostato per pioggia alle 18:00 invece delle 17:30" data-testid="ai-msg-context" />
          </div>
          <div>
            <Label>Tono</Label>
            <Select value={tone} onValueChange={setTone}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="cordiale">Cordiale</SelectItem>
                <SelectItem value="formale">Formale</SelectItem>
                <SelectItem value="amichevole">Amichevole</SelectItem>
                <SelectItem value="motivazionale">Motivazionale</SelectItem>
                <SelectItem value="urgente">Urgente</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button onClick={onRun} disabled={loading} className="w-full bg-sky-500 hover:bg-sky-600" data-testid="ai-msg-generate">
            {loading ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Wand2 size={14} className="mr-1" />}
            {loading ? `Generazione... ${elapsedSeconds}s` : "Genera messaggio"}
          </Button>
        </div>
      </Card>
      <Card className="p-5 border-slate-200">
        <h3 className="font-heading font-semibold mb-2">Messaggio</h3>
        {loading && <AILoadingState label="Generazione messaggio" expectedSeconds={20} />}
        {out && (
          <div className="space-y-3" data-testid="ai-msg-output">
            <Textarea rows={10} value={out} onChange={(e) => setOut(e.target.value)} className="font-mono text-sm" />
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => copyText(out)}><Copy size={12} className="mr-1" /> Copia</Button>
              <Button size="sm" onClick={shareWA} className="bg-emerald-500 hover:bg-emerald-600">Invia su WhatsApp</Button>
            </div>
          </div>
        )}
        {!out && !loading && <p className="text-sm text-slate-400">Il messaggio generato apparirà qui, modificabile prima dell&apos;invio.</p>}
      </Card>
    </div>
  );
}
